/**
 * /instalar — Página pública de instalação do app
 *
 * Objetivo: URL bonita pra divulgação (QR codes, panfletos, redes sociais).
 * Mostra QR code apontando para a própria página + instruções específicas
 * por SO + botão de instalação direta quando disponível.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Download, Smartphone, Monitor, Share2, Plus, MoreVertical,
  Bell, Zap, WifiOff, CheckCircle2, Copy, Apple,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePWAInstall } from '../hooks/usePWAInstall';
import useSiteConfig from '../hooks/useSiteConfig';

function detectOS() {
  if (typeof window === 'undefined') return 'desktop';
  const ua = (navigator.userAgent || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
}

/**
 * Detalha o ambiente iOS para decidir a UX de instalação.
 * - É Safari real? → mostra tutorial normal
 * - É Chrome/Firefox iOS? → Apple bloqueia PWA install nesses browsers.
 *   Precisa abrir em Safari. UI mostra copy-link + abrir Safari direto.
 * - É WebView (Instagram, Facebook, WhatsApp, TikTok)? → mesmo problema,
 *   usuário geralmente nem sabe que está no WebView.
 * - É iPad? → share button fica no topo-direito, não embaixo.
 */
function detectIOSEnvironment() {
  if (typeof window === 'undefined') return {};
  const ua = navigator.userAgent || '';
  const uaL = ua.toLowerCase();
  const isIpad = /ipad/.test(uaL) || (navigator.maxTouchPoints > 1 && /macintosh/.test(uaL));
  const isIphone = /iphone|ipod/.test(uaL);
  // Chrome iOS: CriOS; Firefox iOS: FxiOS; Edge iOS: EdgiOS; Opera iOS: OPiOS/OPT
  const isChromeIOS = /crios/.test(uaL);
  const isFirefoxIOS = /fxios/.test(uaL);
  const isEdgeIOS = /edgios/.test(uaL);
  // In-app browsers (não têm share "Add to Home Screen"):
  const isInstagram = /instagram/.test(uaL);
  const isFacebook = /fban|fbav|fb_iab/.test(uaL);
  const isTikTok = /musical_ly|bytedancewebview/.test(uaL);
  const isWhatsApp = /whatsapp/.test(uaL);
  const isLinkedIn = /linkedin/.test(uaL);
  const isInApp = isInstagram || isFacebook || isTikTok || isWhatsApp || isLinkedIn;
  const isAltBrowser = isChromeIOS || isFirefoxIOS || isEdgeIOS;
  // Safari oficial: nenhuma das acima, mas tem Safari/Version na UA
  const isSafari = !isInApp && !isAltBrowser && /safari\//i.test(ua) && /version\//i.test(ua);
  return {
    isIpad,
    isIphone,
    isInApp,
    isAltBrowser,
    isSafari,
    inAppName: isInstagram ? 'Instagram' : isFacebook ? 'Facebook'
      : isTikTok ? 'TikTok' : isWhatsApp ? 'WhatsApp'
      : isLinkedIn ? 'LinkedIn' : null,
    altBrowserName: isChromeIOS ? 'Chrome' : isFirefoxIOS ? 'Firefox'
      : isEdgeIOS ? 'Edge' : null,
  };
}

const BENEFITS = [
  {
    icon: Zap,
    title: 'Acesso instantâneo',
    text: 'Abre direto pela tela inicial, como qualquer app — sem digitar o site.',
  },
  {
    icon: Bell,
    title: 'Notificações de alertas',
    text: 'Receba avisos da Defesa Civil, promoções e novidades em primeira mão.',
  },
  {
    icon: WifiOff,
    title: 'Funciona offline',
    text: 'Páginas visitadas ficam disponíveis mesmo sem internet.',
  },
  {
    icon: CheckCircle2,
    title: 'Sem espaço no celular',
    text: 'Ocupa pouquíssima memória — não é app de loja, não pesa.',
  },
];

export default function InstalarApp() {
  const { config } = useSiteConfig();
  const { installPrompt, isInstalled, isIOS, install } = usePWAInstall();
  const [os, setOs] = useState('desktop');
  const [installing, setInstalling] = useState(false);
  const [showMobileHelp, setShowMobileHelp] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Vem do fluxo pós-cadastro: /instalar?novo=1 — mostra banner de boas-vindas
  const isNovoUsuario = searchParams.get('novo') === '1';

  // "Lembrar depois" (iter 294): usuário quer instalar mais tarde.
  // Em vez de nunca mais lembrar, aproveitamos a infra existente do
  // PWAInstallBanner — setamos o DISMISS_KEY com cooldown curto de 1 dia
  // (vs 7 dias do dismiss normal). Resultado: a próxima visita ainda
  // mostra o banner flutuante de instalação como lembrete sutil.
  const deferInstall = () => {
    try {
      const ONE_DAY_MS = 24 * 60 * 60 * 1000;
      localStorage.setItem('pwa_banner_dismissed_until', String(Date.now() + ONE_DAY_MS));
      localStorage.setItem('pwa_banner_dismissed_at', String(Date.now()));
    } catch (_) { /* modo privado — segue */ }
    navigate('/');
  };

  useEffect(() => {
    setOs(detectOS());
  }, []);

  // Detalha o ambiente iOS (Safari? WebView? iPad?) — usado pra customizar
  // instruções: WebView precisa abrir no Safari antes; iPad tem share em
  // outra posição; Chrome iOS tem a mesma limitação de WebView.
  const iosEnv = useMemo(() => detectIOSEnvironment(), []);
  const needsSafariFirst = os === 'ios' && (iosEnv.isInApp || iosEnv.isAltBrowser);

  // URL da própria página, usada no QR code
  const currentUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/instalar`;
  }, []);

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(currentUrl);
      toast.success('Link copiado!');
    } catch (e) {
      toast.error('Não foi possível copiar');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Empreende Comunidade',
          text: 'Instale o app oficial da sua cidade',
          url: currentUrl,
        });
      } catch (e) {
        // user cancelled
      }
    } else {
      handleCopyLink();
    }
  };

  const handleInstall = async () => {
    // 1. Android com prompt do Chrome disponível → dispara nativo
    if (installPrompt) {
      setInstalling(true);
      try {
        const ok = await install();
        if (ok) toast.success('App instalado!');
      } finally {
        setInstalling(false);
      }
      return;
    }
    // 2. iOS → abre modal com guia visual. navigator.share abria o share sheet
    //    inteiro (fotos, AirDrop, apps), onde "Adicionar à Tela de Início"
    //    ficava escondido no meio — causava confusão em usuários novos.
    //    Melhor mostrar diretamente o guia visual com arrow apontando o ícone.
    if (isIOS) {
      setShowMobileHelp(true);
      return;
    }
    // 3. Fallback: Android pré-engagement ou browser sem suporte → modal
    //    com instruções bem explícitas apontando pro menu do browser.
    setShowMobileHelp(true);
  };

  const cor = config?.cor_primaria || '#56007F';
  const cor2 = config?.cor_secundaria || '#FFA52A';

  return (
    <>
      <Helmet>
        <title>Instalar app — Empreende Comunidade</title>
        <meta
          name="description"
          content="Instale o aplicativo Empreende Comunidade no seu celular para acessar negócios, promoções e alertas oficiais da sua cidade."
        />
        <meta property="og:title" content="Instale o app Empreende Comunidade" />
        <meta property="og:description" content="Acesso rápido, notificações de alertas e funcionamento offline." />
      </Helmet>

      <div
        className="min-h-screen"
        style={{
          background: `linear-gradient(135deg, ${cor} 0%, ${cor}dd 50%, ${cor}aa 100%)`,
        }}
        data-testid="instalar-page"
      >
        {/* Hero */}
        <section className="px-4 pt-8 pb-12 sm:pt-16">
          <div className="mx-auto max-w-5xl text-center text-white">
            {isNovoUsuario && (
              <div
                className="mx-auto mb-6 max-w-xl rounded-2xl bg-white/15 border border-white/30 px-5 py-4 text-left backdrop-blur-sm"
                data-testid="novo-usuario-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-green-500 p-1.5 shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-white">
                      Bem-vindo(a)! Sua conta foi criada.
                    </p>
                    <p className="mt-0.5 text-xs text-white/90">
                      Agora instale o app pra receber alertas, promoções e novidades
                      direto no seu celular. Leva 30 segundos.
                    </p>
                    <button
                      type="button"
                      onClick={deferInstall}
                      data-testid="btn-lembrar-depois"
                      className="mt-2 inline-flex items-center text-xs font-semibold text-white/80 hover:text-white underline underline-offset-2"
                    >
                      Pular, lembrar depois
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs font-semibold backdrop-blur-sm">
              <Download className="h-3.5 w-3.5" />
              Instale o app oficial
            </div>
            <h1 className="mt-4 text-4xl font-bold sm:text-5xl lg:text-6xl" data-testid="instalar-titulo">
              Tenha a sua cidade<br />
              <span style={{ color: cor2 }}>na palma da mão</span>
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-white/90 sm:text-lg">
              Instale o Empreende Comunidade no seu celular em 30 segundos.
              Sem loja de apps, sem espaço ocupado, sem complicação.
            </p>

            {isInstalled ? (
              <div
                className="mx-auto mt-8 inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3 text-sm font-bold text-white shadow-xl"
                data-testid="instalar-ja-instalado"
              >
                <CheckCircle2 className="h-5 w-5" />
                App já instalado neste dispositivo!
              </div>
            ) : (
              <button
                type="button"
                onClick={handleInstall}
                disabled={installing}
                data-testid="btn-instalar-agora"
                className="mx-auto mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold shadow-xl transition-transform hover:scale-105 disabled:opacity-50"
                style={{ color: cor }}
              >
                {isIOS ? <Share2 className="h-5 w-5" /> : <Download className="h-5 w-5" />}
                {installing
                  ? 'Instalando...'
                  : isIOS
                    ? 'Adicionar ao iPhone'
                    : installPrompt
                      ? 'Instalar agora'
                      : 'Como instalar'}
              </button>
            )}
          </div>
        </section>

        {/* Conteúdo principal */}
        <section className="px-4 pb-12">
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            {/* Instruções por SO — primeiro no mobile (ordem 1), segundo no desktop */}
            <div className="rounded-3xl bg-white p-6 shadow-2xl order-1 md:order-2">
              <Tabs os={os} setOs={setOs} cor={cor} />
              <InstructionContent os={os} cor={cor} iosEnv={iosEnv} onCopyLink={handleCopyLink} />
            </div>

            {/* QR Code — escondido no mobile (uso é desktop → celular) */}
            <div
              className="rounded-3xl bg-white p-6 shadow-2xl order-2 md:order-1 hidden md:block"
              data-testid="qr-card"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-purple-100 p-2.5" style={{ color: cor }}>
                  <Smartphone className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">
                  Aponte a câmera do celular
                </h2>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Use a câmera do seu celular para abrir esta página no celular.
                Não precisa de leitor de QR.
              </p>
              <div className="mt-5 flex flex-col items-center gap-4 rounded-2xl bg-slate-50 p-6">
                {currentUrl && (
                  <div className="rounded-2xl bg-white p-4 shadow-inner">
                    <QRCodeSVG
                      value={currentUrl}
                      size={200}
                      bgColor="#ffffff"
                      fgColor={cor}
                      level="M"
                      includeMargin={false}
                      data-testid="qr-code"
                    />
                  </div>
                )}
                <div className="text-center">
                  <p className="text-xs font-mono text-slate-500 break-all">
                    {currentUrl}
                  </p>
                  <div className="mt-3 flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      data-testid="btn-copiar-link"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <Copy className="h-3 w-3" /> Copiar link
                    </button>
                    <button
                      type="button"
                      onClick={handleShare}
                      data-testid="btn-compartilhar"
                      className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white"
                      style={{ background: cor }}
                    >
                      <Share2 className="h-3 w-3" /> Compartilhar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefícios */}
          <div className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-2xl bg-white/10 p-5 text-white backdrop-blur-sm"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 text-sm font-bold">{title}</h3>
                <p className="mt-1 text-xs text-white/85">{text}</p>
              </div>
            ))}
          </div>

          {/* FAQ rápido */}
          <div className="mx-auto mt-10 max-w-3xl rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900">Perguntas frequentes</h3>
            <div className="mt-4 space-y-3">
              <Faq q="Preciso pagar para instalar?">
                Não. O app é 100% gratuito e oficial da prefeitura.
              </Faq>
              <Faq q="Vai aparecer na minha tela como um app normal?">
                Sim. Vai ter ícone na tela inicial, abre em tela cheia, sem
                barra de endereço do navegador.
              </Faq>
              <Faq q="E se eu quiser desinstalar depois?">
                É só segurar o ícone e arrastar para a lixeira (Android) ou
                deletar como qualquer app (iOS). Você pode reinstalar a
                qualquer momento voltando aqui.
              </Faq>
              <Faq q="Funciona em qualquer celular?">
                Sim, em qualquer Android (5.0+) ou iPhone (iOS 11.3+). No
                Android use o Chrome ou Edge. No iPhone use o Safari.
              </Faq>
              <Faq q="Vai usar muita internet/bateria?">
                Não. Funciona como uma aba do navegador, só consome quando
                você está usando. Sem segundo plano.
              </Faq>
            </div>
          </div>
        </section>

        {/* Modal mobile — "Como instalar" quando o navegador não oferece prompt */}
        {showMobileHelp && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
            onClick={() => setShowMobileHelp(false)}
            data-testid="mobile-help-modal"
          >
            <div
              className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-slate-200 sm:hidden" />
              {isIOS ? (
                // Reusa o componente completo (detecta Safari vs WebView,
                // mostra mockup visual com arrow pulsando no share button
                // e lida com iPad/iPhone separadamente).
                <IOSInstructions cor={cor} iosEnv={iosEnv} onCopyLink={handleCopyLink} />
              ) : (
                <>
                  <h3 className="text-lg font-bold text-slate-900">
                    No Android (Chrome/Edge)
                  </h3>
                  <ol className="mt-4 space-y-3 text-sm text-slate-700">
                    <Step n="1" cor={cor}>
                      Toque no menu{' '}
                      <MoreVertical className="inline h-4 w-4 mx-0.5" style={{ color: cor }} />{' '}
                      do navegador (canto superior direito).
                    </Step>
                    <Step n="2" cor={cor}>
                      Procure por <strong>&ldquo;Instalar app&rdquo;</strong>,{' '}
                      <strong>&ldquo;Adicionar à tela inicial&rdquo;</strong> ou{' '}
                      <strong>&ldquo;Instalar&rdquo;</strong>.
                    </Step>
                    <Step n="3" cor={cor}>
                      Confirme em <strong>Instalar</strong>. O ícone aparecerá
                      na tela inicial do celular.
                    </Step>
                  </ol>
                </>
              )}
              <button
                type="button"
                onClick={() => setShowMobileHelp(false)}
                className="mt-6 w-full rounded-full px-6 py-3 text-sm font-bold text-white"
                style={{ background: cor }}
                data-testid="mobile-help-close"
              >
                Entendi
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Tabs({ os, setOs, cor }) {
  const tabs = [
    { id: 'android', label: 'Android', icon: Smartphone },
    { id: 'ios', label: 'iPhone', icon: Apple },
    { id: 'desktop', label: 'Computador', icon: Monitor },
  ];
  return (
    <div className="flex gap-1 rounded-xl bg-slate-100 p-1" data-testid="os-tabs">
      {tabs.map(({ id, label, icon: Icon }) => {
        const active = os === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setOs(id)}
            data-testid={`tab-${id}`}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs font-semibold transition ${
              active ? 'bg-white shadow' : 'text-slate-600 hover:bg-white/60'
            }`}
            style={active ? { color: cor } : {}}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function InstructionContent({ os, cor, iosEnv = {}, onCopyLink }) {
  if (os === 'ios') {
    return <IOSInstructions cor={cor} iosEnv={iosEnv} onCopyLink={onCopyLink} />;
  }

  if (os === 'android') {
    return (
      <div className="mt-5" data-testid="instrucoes-android">
        <h3 className="text-base font-bold text-slate-900">No Android (Chrome/Edge)</h3>
        <p className="mt-1 text-xs text-slate-500">
          Use o Chrome ou Edge. Firefox não suporta instalação no Android.
        </p>
        <ol className="mt-4 space-y-3 text-sm text-slate-700">
          <Step n="1" cor={cor}>
            Toque em <strong>Instalar agora</strong> acima (se aparecer) ou no
            menu{' '}
            <MoreVertical className="inline h-4 w-4 mx-0.5" style={{ color: cor }} />{' '}
            do navegador.
          </Step>
          <Step n="2" cor={cor}>
            Procure por <strong>&ldquo;Instalar app&rdquo;</strong> ou{' '}
            <strong>&ldquo;Adicionar à tela inicial&rdquo;</strong>.
          </Step>
          <Step n="3" cor={cor}>
            Confirme em <strong>Instalar</strong>. O ícone aparecerá na tela
            inicial do celular.
          </Step>
        </ol>
      </div>
    );
  }

  return (
    <div className="mt-5" data-testid="instrucoes-desktop">
      <h3 className="text-base font-bold text-slate-900">No computador</h3>
      <p className="mt-1 text-xs text-slate-500">Chrome, Edge ou Brave (não funciona no Firefox).</p>
      <ol className="mt-4 space-y-3 text-sm text-slate-700">
        <Step n="1" cor={cor}>
          Procure o ícone{' '}
          <Download className="inline h-4 w-4 mx-0.5" style={{ color: cor }} />{' '}
          de <strong>Instalar</strong> na barra de endereços (à direita).
        </Step>
        <Step n="2" cor={cor}>
          Se não aparecer, abra o menu <strong>⋮</strong> do navegador.
        </Step>
        <Step n="3" cor={cor}>
          Clique em{' '}
          <strong>&ldquo;Instalar Empreende Comunidade&rdquo;</strong> ou{' '}
          <strong>&ldquo;Apps → Instalar este site&rdquo;</strong>.
        </Step>
      </ol>
    </div>
  );
}

function Step({ n, children, cor }) {
  return (
    <li className="flex gap-3">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ background: cor }}
      >
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Faq({ q, children }) {
  return (
    <details className="group rounded-xl border border-slate-200 px-4 py-3">
      <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
        {q}
        <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
      </summary>
      <p className="mt-2 text-sm text-slate-600">{children}</p>
    </details>
  );
}

/**
 * Instruções iOS com guia visual (SVG) e detecção de ambiente.
 * - Chrome/Firefox iOS ou in-app browser (Instagram, FB, WhatsApp): mostra
 *   banner vermelho pedindo pra abrir no Safari + copiar link.
 * - Safari real: tutorial com SVG do bottom bar e share button pulsando.
 * - iPad: ajusta o texto porque o share button fica no topo-direito.
 */
function IOSInstructions({ cor, iosEnv, onCopyLink }) {
  const { isSafari, isAltBrowser, isInApp, isIpad, isIphone, altBrowserName, inAppName } = iosEnv;
  const needsSafari = isAltBrowser || isInApp;

  if (needsSafari) {
    const label = isInApp
      ? `Você está dentro do ${inAppName}`
      : `Você está no ${altBrowserName}`;
    // Deep link iOS: substituindo `https://` por `x-safari-https://` fazemos
    // o iOS entregar a URL direto para o Safari, abrindo-o em primeiro plano.
    // Suportado desde iOS 15 e respeitado por webviews (Instagram, FB,
    // WhatsApp) porque é tratado no nível do sistema operacional. Se o
    // dispositivo não entender, o click não faz nada — o "Copiar link"
    // continua como fallback universal.
    const currentUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/instalar`
      : '';
    const safariDeepLink = currentUrl.replace(/^https?:\/\//, 'x-safari-https://');
    const openInSafari = () => {
      try {
        window.location.href = safariDeepLink;
      } catch (_) { /* webview sem suporte — segue sem crash */ }
    };
    return (
      <div className="mt-5" data-testid="instrucoes-ios-nao-safari">
        <h3 className="text-base font-bold text-slate-900">
          Abra esta página no Safari
        </h3>
        <div className="mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-4">
          <p className="text-sm font-bold text-red-900">
            ⚠️ {label}
          </p>
          <p className="mt-1 text-xs text-red-800">
            A Apple só permite instalar PWA no <strong>Safari</strong>.
            {isInApp
              ? ' Toque no botão abaixo para abrir direto, ou nos três pontinhos (⋯) no canto superior direito e escolha "Abrir no Safari".'
              : ' Toque no botão abaixo ou abra o Safari manualmente e cole o link.'}
          </p>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <a
            href={safariDeepLink}
            onClick={openInSafari}
            data-testid="btn-abrir-safari"
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-white shadow-lg"
            style={{ background: cor }}
          >
            <Apple className="h-4 w-4" />
            Abrir no Safari
          </a>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-xs text-slate-600 font-semibold mb-1">
              Não funcionou? Copie o link e cole manualmente no Safari:
            </p>
            <p className="font-mono text-xs text-slate-800 break-all mb-2">
              {currentUrl}
            </p>
            <button
              type="button"
              onClick={onCopyLink}
              data-testid="btn-copiar-link-safari"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
            >
              <Copy className="h-3.5 w-3.5" /> Copiar link
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Safari real (ou iOS sem detecção clara — vamos assumir Safari)
  const shareLocation = isIpad ? 'no canto superior direito' : 'na barra inferior';
  const shareHint = isIpad
    ? 'logo ao lado da barra de endereços'
    : 'é o quadrado com uma seta pra cima';
  return (
    <div className="mt-5" data-testid="instrucoes-ios">
      <h3 className="text-base font-bold text-slate-900">
        {isIpad ? 'No iPad (Safari)' : 'No iPhone (Safari)'}
      </h3>
      {isSafari === false && (
        <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ Confirme que está usando o <strong>Safari</strong> — outros navegadores não funcionam.
        </p>
      )}
      <IPhoneSafariMockup cor={cor} isIpad={isIpad} />
      <ol className="mt-4 space-y-3 text-sm text-slate-700">
        <Step n="1" cor={cor}>
          Toque no ícone de <strong>Compartilhar</strong>{' '}
          <Share2 className="inline h-4 w-4 mx-0.5" style={{ color: cor }} />{' '}
          {shareLocation} ({shareHint}).
        </Step>
        <Step n="2" cor={cor}>
          Role a lista até achar{' '}
          <strong>&ldquo;Adicionar à Tela de Início&rdquo;</strong>{' '}
          <Plus className="inline h-4 w-4 mx-0.5" style={{ color: cor }} />{' '}
          e toque nela.
        </Step>
        <Step n="3" cor={cor}>
          Confirme em <strong>Adicionar</strong> no canto superior direito.
          Pronto! O ícone aparece na sua tela inicial.
        </Step>
      </ol>
    </div>
  );
}

/**
 * Mockup SVG estilizado do Safari no iPhone/iPad mostrando a posição
 * do botão de compartilhar com um "pulse" + arrow apontando.
 * Pura CSS/SVG — sem imagens externas, pequeno (~2KB).
 */
function IPhoneSafariMockup({ cor, isIpad }) {
  if (isIpad) {
    // iPad: share button fica top-right na toolbar
    return (
      <div className="mt-4 rounded-2xl bg-gradient-to-b from-slate-100 to-slate-200 p-4" data-testid="ios-mockup-ipad">
        <div className="rounded-xl bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 border-b border-slate-200">
            <span className="h-2 w-2 rounded-full bg-red-400" />
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            <span className="h-2 w-2 rounded-full bg-green-400" />
            <div className="flex-1 ml-2 h-5 rounded bg-slate-100 flex items-center px-2 text-[9px] text-slate-500 font-mono">
              empontenova.com.br/instalar
            </div>
            {/* Share button alvo */}
            <div className="relative">
              <div
                className="h-6 w-6 rounded flex items-center justify-center shadow-lg"
                style={{ background: cor }}
              >
                <Share2 className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="absolute -inset-1 rounded animate-ping opacity-60" style={{ background: cor }} />
              <ArrowPointer className="absolute -bottom-6 -right-1 rotate-45" cor={cor} />
            </div>
          </div>
          <div className="p-3 text-center text-[10px] text-slate-400">conteúdo do site…</div>
        </div>
      </div>
    );
  }
  // iPhone: share button fica no bottom toolbar
  return (
    <div className="mt-4 rounded-2xl bg-gradient-to-b from-slate-100 to-slate-200 p-4" data-testid="ios-mockup-iphone">
      <div className="mx-auto max-w-[180px] rounded-[28px] bg-black p-1.5 shadow-xl">
        <div className="rounded-[22px] bg-white overflow-hidden">
          <div className="h-5 bg-black" />
          <div className="h-16 bg-slate-100 flex items-center justify-center text-[10px] text-slate-400">
            conteúdo do site
          </div>
          <div className="h-8 bg-slate-50 border-t border-slate-200 flex items-center justify-around px-2">
            <span className="text-slate-300 text-xs">&larr;</span>
            <span className="text-slate-300 text-xs">&rarr;</span>
            {/* Share button alvo */}
            <div className="relative">
              <div
                className="h-6 w-6 rounded flex items-center justify-center shadow-lg"
                style={{ background: cor }}
              >
                <Share2 className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="absolute -inset-1 rounded animate-ping opacity-60" style={{ background: cor }} />
            </div>
            <span className="text-slate-300 text-xs">▦</span>
            <span className="text-slate-300 text-xs">▭</span>
          </div>
        </div>
      </div>
      <div className="mt-2 text-center">
        <ArrowPointer cor={cor} className="inline-block rotate-180" />
        <p className="text-xs font-bold mt-1" style={{ color: cor }}>
          Toque aqui
        </p>
      </div>
    </div>
  );
}

function ArrowPointer({ cor, className = '' }) {
  return (
    <svg
      className={className}
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke={cor}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v14" />
      <path d="M5 10l7 7 7-7" />
    </svg>
  );
}

